<script setup lang="ts">
import { onMounted, onUnmounted, computed, watch } from 'vue'
import { useIdentityStore } from '@/stores/identity'
import { useOnboardingStore } from '@/stores/onboarding'
import { useGateStore } from '@/stores/gate'
import { startVersionCheck } from '@/lib/versionCheck'
import { trackEvent } from '@/lib/trackEvent'
import { initAbandonBeacon } from '@/lib/abandonBeacon'
import AppShell from '@/components/AppShell.vue'
import AuthGate from '@/components/AuthGate.vue'
import DevTestLogin from '@/components/DevTestLogin.vue'
import LoadingScreen from '@/components/LoadingScreen.vue'
import CompletionToast from '@/components/CompletionToast.vue'
import Toast from '@/components/Toast.vue'
import OnboardingModal from '@/components/OnboardingModal.vue'
import SubscribeGate from '@/components/SubscribeGate.vue'

const identity = useIdentityStore()
const onboarding = useOnboardingStore()
const gate = useGateStore()
const isLoading = computed(() => !identity.initialized || identity.authPending)
const showGate = computed(() => identity.initialized && identity.unauthorized)
const showDevTest = computed(
  () => identity.initialized && !identity.actor && identity.devTestAvailable,
)
const showOnboarding = computed(
  () => identity.initialized && !identity.unauthorized && !onboarding.seen,
)
// While the platform is telegram, hold the loading screen until the gate
// status is known (prevents an app flash before the subscribe check lands).
const showLoading = computed(() => isLoading.value || (identity.platform === 'telegram' && gate.state === 'unknown'))
const showSubscribeGate = computed(() => identity.initialized && gate.state === 'blocked')

function handleSessionExpired() {
  identity.expire()
  identity.init()
}

// Once auth completes, run the initial subscription check for the gate.
watch(
  () => identity.actor,
  (actor) => {
    if (actor) gate.check()
  },
  { immediate: true },
)

onMounted(() => {
  identity.init()
  window.addEventListener('botsuite:session-expired', handleSessionExpired)
  startVersionCheck()
  initAbandonBeacon()
  trackEvent('app-opened')
})

onUnmounted(() => {
  window.removeEventListener('botsuite:session-expired', handleSessionExpired)
  gate.dispose()
})
</script>

<template>
  <LoadingScreen v-if="showLoading" />
  <DevTestLogin v-else-if="showDevTest" />
  <AuthGate v-else-if="showGate" />
  <SubscribeGate v-else-if="showSubscribeGate" />
  <AppShell v-else>
    <CompletionToast />
    <Toast />
    <router-view v-slot="{ Component }">
      <KeepAlive :include="['AssistantChatView', 'OrdersView', 'AboutView', 'SupportView']">
        <component :is="Component" />
      </KeepAlive>
    </router-view>
    <OnboardingModal v-if="showOnboarding" />
  </AppShell>
</template>


